import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { MapPin, ChevronsUpDown, Check, Globe, Building2, Home } from 'lucide-react';
import { COUNTRY_OPTIONS, KOTAS_BY_COUNTRY, KABUPATENS_BY_KOTA, splitCity, composeCity } from '@/data/locations';

const btnCls = "flex items-center justify-between w-full t-bg-input t-border h-10 rounded-lg text-sm px-4 t-text";

function ComboButton({ value, placeholder, Icon, testId, onOpen, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={disabled ? undefined : onOpen}
      disabled={disabled}
      className={`${btnCls} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className="flex items-center gap-2 truncate">
        <Icon className="w-3.5 h-3.5 t-text-muted flex-shrink-0" />
        <span className={value ? 't-text' : 't-text-muted'}>{value || placeholder}</span>
      </span>
      <ChevronsUpDown className="w-3.5 h-3.5 t-text-muted flex-shrink-0" />
    </button>
  );
}

function ComboList({ items, value, onSelect, emptyLabel, searchPlaceholder, allowClear = true }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? items.filter(i => i.toLowerCase().includes(search.toLowerCase()))
    : items;
  // Stop wheel/touch events from bubbling to the parent Dialog which otherwise
  // swallows the scroll — this caused the dropdown to feel "stuck" when opened
  // inside the Admin Create/Edit member modal.
  const stopScroll = (e) => e.stopPropagation();
  return (
    <Command className="bg-transparent" shouldFilter={false}>
      <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} className="t-text" />
      <CommandList
        className="max-h-[260px] overflow-y-auto"
        style={{ overscrollBehavior: 'contain' }}
        onWheel={stopScroll}
        onTouchMove={stopScroll}
      >
        <CommandEmpty className="t-text-muted text-sm py-4 text-center">{emptyLabel}</CommandEmpty>
        <CommandGroup>
          {allowClear && value && (
            <CommandItem value="__clear__" onSelect={() => { onSelect(''); setSearch(''); }} className="t-text-muted text-sm">
              Clear selection
            </CommandItem>
          )}
          {filtered.map(item => (
            <CommandItem key={item} value={item} onSelect={() => { onSelect(item); setSearch(''); }} className="t-text text-sm">
              <Check className={`w-3.5 h-3.5 mr-2 ${value === item ? 'opacity-100' : 'opacity-0'}`} />
              {item}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/**
 * Location input — 4 fields, only Negara (country) is required.
 *
 * Props:
 *   value: { country, city, location_detail }
 *     • `city` is the compound "Kota - Kabupaten" string kept for backend
 *       backward compat. This component splits it internally on render.
 *   onChange: (patch) => void — patch may include country / city / location_detail
 *   lang: 'en' | 'id'
 */
export function LocationInput({ value, onChange, lang, testIdPrefix = 'location' }) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [kotaOpen, setKotaOpen] = useState(false);
  const [kabOpen, setKabOpen] = useState(false);
  const isID = lang !== 'en';

  const { kota, kabupaten } = useMemo(() => splitCity(value?.city || ''), [value?.city]);

  const kotaOptions = useMemo(() => {
    if (!value?.country) return [];
    return KOTAS_BY_COUNTRY[value.country] || [];
  }, [value?.country]);

  const kabupatenOptions = useMemo(() => {
    if (!value?.country || !kota) return [];
    return KABUPATENS_BY_KOTA[`${value.country}|${kota}`] || [];
  }, [value?.country, kota]);

  const setCountry = (v) => {
    // When country changes, reset kota+kabupaten if they no longer belong.
    const nextKotas = v ? (KOTAS_BY_COUNTRY[v] || []) : [];
    const stillValid = kota && nextKotas.includes(kota);
    const nextKota = stillValid ? kota : '';
    const nextKab = stillValid ? kabupaten : '';
    onChange({ country: v, city: composeCity({ kota: nextKota, kabupaten: nextKab }) });
    setCountryOpen(false);
  };

  const setKota = (v) => {
    // Kota change resets kabupaten (city-detail no longer applicable).
    onChange({ city: composeCity({ kota: v, kabupaten: '' }) });
    setKotaOpen(false);
  };

  const setKabupaten = (v) => {
    onChange({ city: composeCity({ kota, kabupaten: v }) });
    setKabOpen(false);
  };

  return (
    <div className="space-y-2">
      {/* Row 1: Negara (wajib) */}
      <Popover open={countryOpen} onOpenChange={setCountryOpen}>
        <PopoverTrigger asChild>
          <div>
            <ComboButton
              value={value?.country || ''}
              placeholder={isID ? 'Negara *' : 'Country *'}
              Icon={Globe}
              testId={`${testIdPrefix}-country`}
              onOpen={() => setCountryOpen(true)}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0 glass-card" align="start">
          <ComboList
            items={COUNTRY_OPTIONS}
            value={value?.country || ''}
            onSelect={setCountry}
            emptyLabel={isID ? 'Negara tidak ditemukan' : 'Country not found'}
            searchPlaceholder={isID ? 'Cari negara...' : 'Search country...'}
          />
        </PopoverContent>
      </Popover>

      {/* Row 2: Kota + Kabupaten (both opsional) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Popover open={kotaOpen} onOpenChange={setKotaOpen}>
          <PopoverTrigger asChild>
            <div>
              <ComboButton
                value={kota}
                placeholder={value?.country
                  ? (isID ? 'Kota (opsional)' : 'City (optional)')
                  : (isID ? 'Pilih negara dulu' : 'Select country first')}
                Icon={Building2}
                testId={`${testIdPrefix}-kota`}
                disabled={!value?.country}
                onOpen={() => setKotaOpen(true)}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 glass-card" align="start">
            <ComboList
              items={kotaOptions}
              value={kota}
              onSelect={setKota}
              emptyLabel={isID ? 'Kota tidak ditemukan' : 'City not found'}
              searchPlaceholder={isID ? 'Cari kota...' : 'Search city...'}
            />
          </PopoverContent>
        </Popover>

        <Popover open={kabOpen} onOpenChange={setKabOpen}>
          <PopoverTrigger asChild>
            <div>
              <ComboButton
                value={kabupaten}
                placeholder={kota
                  ? (kabupatenOptions.length > 0
                    ? (isID ? 'Kabupaten (opsional)' : 'District (optional)')
                    : (isID ? 'Kabupaten (tidak tersedia)' : 'District (n/a)'))
                  : (isID ? 'Pilih kota dulu' : 'Select city first')}
                Icon={Home}
                testId={`${testIdPrefix}-kabupaten`}
                disabled={!kota || kabupatenOptions.length === 0}
                onOpen={() => setKabOpen(true)}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 glass-card" align="start">
            <ComboList
              items={kabupatenOptions}
              value={kabupaten}
              onSelect={setKabupaten}
              emptyLabel={isID ? 'Tidak ada daftar kabupaten' : 'No district list'}
              searchPlaceholder={isID ? 'Cari kabupaten...' : 'Search district...'}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Row 3: Detail lokasi (opsional) */}
      <div className="flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5 t-text-muted flex-shrink-0" />
        <Input
          data-testid={`${testIdPrefix}-detail`}
          value={value?.location_detail || ''}
          onChange={e => onChange({ location_detail: e.target.value })}
          className="t-bg-input t-border h-10 rounded-lg text-sm px-3 t-text flex-1"
          placeholder={isID
            ? 'Detail lokasi (kecamatan / kelurahan / kompleks — opsional)'
            : 'Location detail (district / neighborhood — optional)'}
        />
      </div>

      <p className="text-[11px] t-text-muted">
        {isID
          ? 'Hanya Negara yang wajib diisi. Kota & Kabupaten opsional.'
          : 'Only Country is required. City & District are optional.'}
      </p>
    </div>
  );
}
